import React, { useState, useEffect, useMemo } from "react";
import { ButtonGroup, Button, Row, Col, Container, Card, Form, Badge } from "react-bootstrap";
import axios from "axios";
import config from "../../config";
import { getToken, resetUserSession } from "../../service/AuthService";
import { debounce } from "lodash";

// ============================================================================
// CONFIGURATION SCHEMA - Defines which fields belong to which persona
// ============================================================================
const CONFIG_SCHEMA = {
  // TRADER - Trading execution, risk management, and market timing
  trader: {
    title: "Trading Bot - Trader Controls",
    icon: "📊",
    description: "Position sizing, risk limits, and market timing",
    fields: {
      // Core Trading
      symbol: { label: "Symbol", type: "text", category: "Core" },
      qty_per_trade: { label: "Quantity Per Trade", type: "number", category: "Core" },
      max_position: { label: "Max Position Size", type: "number", category: "Core" },
      max_orders: { label: "Max Concurrent Orders", type: "number", category: "Core" },
      
      // Risk Management
      stop_loss_long: { label: "Stop Loss (Long)", type: "number", category: "Risk" },
      stop_loss_short: { label: "Stop Loss (Short)", type: "number", category: "Risk" },
      min_profit: { label: "Min Profit Target", type: "number", category: "Risk" },
      
      // Market Timing
      start_of_trade: { label: "Trading Start Time", type: "time", category: "Timing" },
      end_of_trade: { label: "Trading End Time", type: "time", category: "Timing" },
      out_of_market: { label: "Market Exit Time", type: "time", category: "Timing" },
      
      // Order Execution
      order_type: { label: "Order Type", type: "select", options: ["limit", "market"], category: "Execution" },
      order_wait: { label: "Order Wait Time", type: "text", category: "Execution" },
      buffer: { label: "Price Buffer", type: "number", category: "Execution" },
    }
  },

  // DATA SCIENTIST - Model configuration and AI parameters
  data_scientist: {
    title: "Trading Bot - AI/ML Configuration",
    icon: "🤖",
    description: "Model selection, prediction parameters, and AI thresholds",
    fields: {
      // Model Selection
      model: { label: "ML Model", type: "text", category: "Model" },
      model_key: { label: "Model Version", type: "text", category: "Model" },
      gen_ai_model_vendor: { label: "Gen AI Vendor", type: "select", options: ["google", "openai", "anthropic"], category: "Model" },
      gen_ai_model_id: { label: "Gen AI Model ID", type: "text", category: "Model" },
      
      // Prediction Config
      predict_input: { label: "Prediction Input", type: "select", options: ["minute_bar", "tick", "quote"], category: "Prediction" },
      gen_ai_pred_len: { label: "Prediction Length", type: "number", category: "Prediction" },
      gen_ai_seq_len: { label: "Sequence Length", type: "number", category: "Prediction" },
      gen_ai_top_k: { label: "Top K Predictions", type: "number", category: "Prediction" },
      
      // Thresholds
      gen_ai_conf_thresh: { label: "Confidence Threshold", type: "number", category: "Thresholds" },
      rsi_th: { label: "RSI Threshold", type: "number", category: "Thresholds" },
      hold_threshold: { label: "Hold Threshold", type: "number", category: "Thresholds" },
      
      // Feature Engineering
      min_bars: { label: "Min Bars Required", type: "number", category: "Features" },
      pressure_window: { label: "Pressure Window", type: "number", category: "Features" },
      water_mark_window: { label: "Watermark Window", type: "number", category: "Features" },
    }
  },

  // ENGINEER - System configuration and API endpoints
  engineer: {
    title: "Trading Bot - System Configuration",
    icon: "⚙️",
    description: "API endpoints, system behavior, and technical settings",
    fields: {
      // System Control
      play: { label: "Bot Status", type: "select", options: ["active", "pause"], category: "Control" },
      mode: { label: "Trading Mode", type: "select", options: ["both", "long", "short"], category: "Control" },
      start_mode: { label: "Start Mode", type: "select", options: ["auto", "manual"], category: "Control" },
      
      // DRL Endpoint
      drl_url: { label: "DRL Prediction URL", type: "text", category: "DRL API" },
      drl_api_key: { label: "DRL API Key", type: "password", category: "DRL API" },
      apply_drl: { label: "Apply DRL", type: "checkbox", category: "DRL API" },
      
      // Prediction Endpoint
      prediction_url: { label: "Prediction URL", type: "text", category: "Prediction API" },
      prediction_api_key: { label: "Prediction API Key", type: "password", category: "Prediction API" },
      
      // Trade Outcome Endpoint
      trade_outcome_url: { label: "Trade Outcome URL", type: "text", category: "Outcome API" },
      trade_outcome_api_key: { label: "Trade Outcome API Key", type: "password", category: "Outcome API" },
      trade_outcome_enabled: { label: "Enable Outcome Tracking", type: "checkbox", category: "Outcome API" },
      trade_outcome_retry_attempts: { label: "Retry Attempts", type: "number", category: "Outcome API" },
      trade_outcome_batch_size: { label: "Batch Size", type: "number", category: "Outcome API" },
      
      // System Behavior
      stale_window: { label: "Stale Data Window", type: "text", category: "System" },
      trade_pub: { label: "Trade Publishing", type: "checkbox", category: "System" },
      publish_c2: { label: "Publish to C2", type: "checkbox", category: "System" },
    }
  },

  // EVALUATION AGENT - Separate card for evaluation agent configuration
  evaluation_agent: {
    title: "Evaluation Agent Configuration",
    icon: "🔍",
    description: "AI-powered position evaluation and monitoring",
    fields: {
      eval_agent_url: { label: "Evaluation URL", type: "text", category: "API" },
      eval_agent_api_key: { label: "Evaluation API Key", type: "password", category: "API" },
      eval_agent_interval_minutes: { label: "Evaluation Interval (min)", type: "number", category: "Timing" },
      eval_agent_min_interval_minutes: { label: "Min Interval (min)", type: "number", category: "Timing" },
      eval_agent_max_interval_minutes: { label: "Max Interval (min)", type: "number", category: "Timing" },
      eval_agent_emergency_conditions: { label: "Emergency Conditions", type: "checkbox", category: "Behavior" },
    }
  }
};

// ============================================================================
// CONFIGURATION CARD COMPONENT
// ============================================================================
const ConfigCard = ({ schema, config, onUpdate, agentType }) => {
  // Group fields by category
  const categorizedFields = useMemo(() => {
    const categories = {};
    Object.entries(schema.fields).forEach(([key, field]) => {
      const category = field.category || "Other";
      if (!categories[category]) categories[category] = [];
      categories[category].push({ key, ...field });
    });
    return categories;
  }, [schema.fields]);

  const handleChange = (key, value, fieldType) => {
    let processedValue = value;
    
    if (fieldType === "checkbox") {
      processedValue = value === "true" || value === true;
    } else if (fieldType === "number") {
      processedValue = value === "" ? "" : value;
    }
    
    onUpdate(key, processedValue);
  };

  const renderField = (key, field) => {
    const value = config[key] !== undefined ? config[key] : "";
    
    if (field.type === "checkbox") {
      return (
        <Form.Check
          type="switch"
          checked={value === true || value === "true"}
          onChange={(e) => handleChange(key, e.target.checked, field.type)}
          style={{ 
            backgroundColor: "#1a1a1a",
            border: "1px solid #333"
          }}
        />
      );
    } else if (field.type === "select") {
      return (
        <Form.Select
          value={value}
          onChange={(e) => handleChange(key, e.target.value, field.type)}
          size="sm"
          style={{
            backgroundColor: "#1a1a1a",
            color: "#ffffff",
            border: "1px solid #333",
            fontSize: "0.85rem"
          }}
        >
          {field.options?.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </Form.Select>
      );
    } else if (field.type === "password") {
      return (
        <Form.Control
          type="password"
          value={value}
          onChange={(e) => handleChange(key, e.target.value, field.type)}
          size="sm"
          style={{
            backgroundColor: "#1a1a1a",
            color: "#ffffff",
            border: "1px solid #333",
            fontSize: "0.75rem",
            fontFamily: "monospace"
          }}
        />
      );
    } else {
      return (
        <Form.Control
          type={field.type || "text"}
          value={value}
          onChange={(e) => handleChange(key, e.target.value, field.type)}
          size="sm"
          style={{
            backgroundColor: "#1a1a1a",
            color: "#ffffff",
            border: "1px solid #333",
            fontSize: "0.85rem"
          }}
        />
      );
    }
  };

  return (
    <Card 
      className="mb-3" 
      style={{ 
        backgroundColor: "#0a0a0a", 
        border: "1px solid #222",
        height: "100%"
      }}
    >
      <Card.Header style={{ 
        backgroundColor: "#000000", 
        borderBottom: "1px solid #222",
        padding: "0.75rem 1rem"
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <span style={{ fontSize: "1.2rem", marginRight: "0.5rem" }}>{schema.icon}</span>
            <strong style={{ color: "#ffffff" }}>{schema.title}</strong>
          </div>
          <Badge bg="secondary" style={{ fontSize: "0.7rem" }}>
            {Object.keys(schema.fields).length} fields
          </Badge>
        </div>
        <div style={{ fontSize: "0.75rem", color: "#888", marginTop: "0.25rem" }}>
          {schema.description}
        </div>
      </Card.Header>
      <Card.Body style={{ padding: "1rem", maxHeight: "600px", overflowY: "auto" }}>
        {Object.entries(categorizedFields).map(([category, fields]) => (
          <div key={category} className="mb-3">
            <div style={{ 
              fontSize: "0.7rem", 
              color: "#00d4ff",
              textTransform: "uppercase",
              marginBottom: "0.5rem",
              fontWeight: "600",
              letterSpacing: "0.5px"
            }}>
              {category}
            </div>
            {fields.map(field => (
              <Row key={field.key} className="mb-2 align-items-center">
                <Col xs={5}>
                  <Form.Label 
                    style={{ 
                      fontSize: "0.8rem", 
                      color: "#ccc",
                      marginBottom: "0"
                    }}
                  >
                    {field.label}
                  </Form.Label>
                </Col>
                <Col xs={7}>
                  {renderField(field.key, field)}
                </Col>
              </Row>
            ))}
          </div>
        ))}
      </Card.Body>
    </Card>
  );
};

// ============================================================================
// MAIN ADMIN PAGE COMPONENT
// ============================================================================
function AdminPage() {
  const [isTrading, setIsTrading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [activePersona, setActivePersona] = useState("trader");
  const debounceDelay = 500;
  const [cfg, setCfg] = useState({});

  const APIUrl = config.API_SOCKET_URL;

  // -----------------------------------------------------------------------
  // API Functions
  // -----------------------------------------------------------------------
  function getBackendAPI(resource, msg) {
    try {
      let resource = "trade/config";
      const response = axios.get(APIUrl + "/" + resource, {
        headers: {
          "x-api-key": config.API_KEY,
          authorizationToken: getToken(),
        },
      });
      return response;
    } catch (error) {
      console.log("Error: Not Authorized", error);
      return error;
    }
  }

  async function callBackendAPI(resource, msg) {
    try {
      const response = await axios.post(APIUrl + "/" + resource, msg, {
        headers: {
          "x-api-key": config.API_KEY,
          authorizationToken: getToken(),
        },
      });
      console.log("Message: ", response);
      return response;
    } catch (error) {
      console.log("Error from API - ", error);
      setErrorMessage("Error getting data:" + error);
      resetUserSession();

      if (error.response) {
        if (error.response.status === 401) {
          console.log("Unable to access API - 401: ", resource);
          alert("Unable to access API - 401");
        } else {
          alert("Unable to access API - password Expired");
          window.location.href = "/Login";
          console.log(
            "API returned an error -",
            error.response.status,
            error.response.data
          );
        }
      } else if (error.request) {
        console.log("Network error - please login");
      } else {
        console.log("Request error -", error.message);
      }
      return null;
    }
  }

  function putBackendAPI(msg) {
    axios
      .put(APIUrl + "/trade/config", msg, {
        headers: {
          "x-api-key": config.API_KEY,
          authorizationToken: getToken(),
        },
      })
      .then((response) => {
        console.log(response.data);
        return response.data;
      })
      .catch((error) => {
        console.log(error);
        return error;
      });
  }

  const debouncedHandleValueChange = debounce((key, value) => {
    setCfg((prevState) => {
      const newState = { ...prevState };
      newState[key] = value;
      let msg = {
        config: newState,
      };
      putBackendAPI(msg);
      return newState;
    });
  }, debounceDelay);

  // -----------------------------------------------------------------------
  // Trading Control Functions
  // -----------------------------------------------------------------------
  const startTrading = async () => {
    let msg = { action: "start" };
    let resource = "trade/action";
    let resp = await callBackendAPI(resource, msg);
    console.log("Response:", resp);
    if (resp && resp.status === 200) {
      setIsTrading(true);
      setErrorMessage(null);
    } else {
      setErrorMessage("Error starting trade server");
    }
  };

  const stopTrading = async () => {
    let msg = { action: "stop" };
    let resource = "trade/action";
    let resp = await callBackendAPI(resource, msg);
    console.log("Response:", resp);
    if (resp && resp.status === 200) {
      setIsTrading(false);
      setIsPaused(false);
      setErrorMessage(null);
    } else {
      setErrorMessage("Error stopping trade server");
    }
  };

  const pauseTrading = () => {
    setIsPaused(true);
    setCfg((prevState) => {
      const newState = { ...prevState };
      newState["play"] = "pause";
      let msg = { config: newState };
      putBackendAPI(msg);
      return newState;
    });
  };

  const resumeTrading = () => {
    setIsPaused(false);
    setCfg((prevState) => {
      const newState = { ...prevState };
      newState["play"] = "active";
      let msg = { config: newState };
      putBackendAPI(msg);
      return newState;
    });
  };

  const handleConfigUpdate = (key, value) => {
    setCfg((prevState) => ({
      ...prevState,
      [key]: value
    }));
    debouncedHandleValueChange(key, value);
  };

  // -----------------------------------------------------------------------
  // Initialize Configuration
  // -----------------------------------------------------------------------
  useEffect(() => {
    let resource = "trade/config";
    let resp = getBackendAPI(resource, null);
    resp
      .then((response) => {
        console.log(response.data);
        setCfg(response.data.msg);
      })
      .catch((error) => {
        console.error(error);
        setErrorMessage("Error: Not Authorized");
        if (error.response && error.response.status === 401) {
          console.log("Not authorized to use this page: ", error.response.statusText);
          setErrorMessage("Error: Not Authorized " + error.response.statusText);
          alert(error.response.statusText);
        } else if (error.response) {
          console.log(
            "API returned an error -",
            error.response.status,
            error.response.data
          );
        }
      });
  }, []);

  // -----------------------------------------------------------------------
  // Status Badge Component
  // -----------------------------------------------------------------------
  const StatusBadge = ({ status, isPaused }) => {
    let bgColor, text;
    if (!status) {
      bgColor = "#dc3545";
      text = "STOPPED";
    } else if (isPaused) {
      bgColor = "#ffc107";
      text = "PAUSED";
    } else {
      bgColor = "#28a745";
      text = "ACTIVE";
    }

    return (
      <Badge 
        style={{ 
          backgroundColor: bgColor,
          fontSize: "0.9rem",
          padding: "0.4rem 0.8rem"
        }}
      >
        {text}
      </Badge>
    );
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  return (
    <div style={{ 
      backgroundColor: "#000000", 
      minHeight: "100vh",
      padding: "1.5rem"
    }}>
      <Container fluid>
        {/* Header Section */}
        <Card className="mb-3" style={{ backgroundColor: "#0a0a0a", border: "1px solid #222" }}>
          <Card.Body className="py-3">
            <Row className="align-items-center">
              <Col md={4}>
                <h4 className="mb-0" style={{ color: "#ffffff" }}>
                  🎛️ Trading System Control
                </h4>
              </Col>
              <Col md={4} className="text-center">
                <div style={{ color: "#888", fontSize: "0.8rem", marginBottom: "0.25rem" }}>
                  System Status
                </div>
                <StatusBadge status={isTrading} isPaused={isPaused} />
              </Col>
              <Col md={4} className="text-end">
                {isTrading ? (
                  <ButtonGroup>
                    {isPaused ? (
                      <Button 
                        variant="success" 
                        onClick={resumeTrading}
                        style={{ fontSize: "0.85rem" }}
                      >
                        ▶️ Resume
                      </Button>
                    ) : (
                      <Button 
                        variant="warning" 
                        onClick={pauseTrading}
                        style={{ fontSize: "0.85rem" }}
                      >
                        ⏸️ Pause
                      </Button>
                    )}
                    <Button 
                      variant="danger" 
                      onClick={stopTrading}
                      style={{ fontSize: "0.85rem" }}
                    >
                      ⏹️ Stop
                    </Button>
                  </ButtonGroup>
                ) : (
                  <Button 
                    variant="success" 
                    onClick={startTrading}
                    style={{ fontSize: "0.85rem" }}
                  >
                    ▶️ Start Trading
                  </Button>
                )}
              </Col>
            </Row>
            {errorMessage && (
              <Row className="mt-2">
                <Col>
                  <div style={{ 
                    color: errorMessage.includes("Error") ? "#dc3545" : "#28a745",
                    fontSize: "0.85rem",
                    textAlign: "center"
                  }}>
                    {errorMessage}
                  </div>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>

        {/* Persona Selector */}
        <Card className="mb-3" style={{ backgroundColor: "#0a0a0a", border: "1px solid #222" }}>
          <Card.Body className="py-2">
            <Row className="align-items-center">
              <Col md={3}>
                <span style={{ color: "#888", fontSize: "0.85rem" }}>View Configuration As:</span>
              </Col>
              <Col md={9}>
                <ButtonGroup style={{ width: "100%" }}>
                  <Button
                    variant={activePersona === "trader" ? "primary" : "outline-secondary"}
                    onClick={() => setActivePersona("trader")}
                    style={{ 
                      fontSize: "0.85rem",
                      flex: 1,
                      backgroundColor: activePersona === "trader" ? "#00d4ff" : "transparent",
                      borderColor: "#333",
                      color: activePersona === "trader" ? "#000" : "#ccc"
                    }}
                  >
                    📊 Trader
                  </Button>
                  <Button
                    variant={activePersona === "data_scientist" ? "primary" : "outline-secondary"}
                    onClick={() => setActivePersona("data_scientist")}
                    style={{ 
                      fontSize: "0.85rem",
                      flex: 1,
                      backgroundColor: activePersona === "data_scientist" ? "#00d4ff" : "transparent",
                      borderColor: "#333",
                      color: activePersona === "data_scientist" ? "#000" : "#ccc"
                    }}
                  >
                    🤖 Data Scientist
                  </Button>
                  <Button
                    variant={activePersona === "engineer" ? "primary" : "outline-secondary"}
                    onClick={() => setActivePersona("engineer")}
                    style={{ 
                      fontSize: "0.85rem",
                      flex: 1,
                      backgroundColor: activePersona === "engineer" ? "#00d4ff" : "transparent",
                      borderColor: "#333",
                      color: activePersona === "engineer" ? "#000" : "#ccc"
                    }}
                  >
                    ⚙️ Engineer
                  </Button>
                  <Button
                    variant={activePersona === "all" ? "primary" : "outline-secondary"}
                    onClick={() => setActivePersona("all")}
                    style={{ 
                      fontSize: "0.85rem",
                      flex: 1,
                      backgroundColor: activePersona === "all" ? "#00d4ff" : "transparent",
                      borderColor: "#333",
                      color: activePersona === "all" ? "#000" : "#ccc"
                    }}
                  >
                    🔧 All Settings
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Configuration Cards */}
        <Row>
          {activePersona === "all" ? (
            <>
              <Col lg={6} className="mb-3">
                <ConfigCard 
                  schema={CONFIG_SCHEMA.trader} 
                  config={cfg}
                  onUpdate={handleConfigUpdate}
                  agentType="trading"
                />
              </Col>
              <Col lg={6} className="mb-3">
                <ConfigCard 
                  schema={CONFIG_SCHEMA.data_scientist} 
                  config={cfg}
                  onUpdate={handleConfigUpdate}
                  agentType="trading"
                />
              </Col>
              <Col lg={6} className="mb-3">
                <ConfigCard 
                  schema={CONFIG_SCHEMA.engineer} 
                  config={cfg}
                  onUpdate={handleConfigUpdate}
                  agentType="trading"
                />
              </Col>
              <Col lg={6} className="mb-3">
                <ConfigCard 
                  schema={CONFIG_SCHEMA.evaluation_agent} 
                  config={cfg}
                  onUpdate={handleConfigUpdate}
                  agentType="evaluation"
                />
              </Col>
            </>
          ) : (
            <Col lg={12}>
              <Row>
                <Col lg={activePersona === "engineer" ? 12 : 6} className="mb-3">
                  <ConfigCard 
                    schema={CONFIG_SCHEMA[activePersona]} 
                    config={cfg}
                    onUpdate={handleConfigUpdate}
                    agentType="trading"
                  />
                </Col>
                {activePersona !== "engineer" && (
                  <Col lg={6} className="mb-3">
                    <ConfigCard 
                      schema={CONFIG_SCHEMA.evaluation_agent} 
                      config={cfg}
                      onUpdate={handleConfigUpdate}
                      agentType="evaluation"
                    />
                  </Col>
                )}
              </Row>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
}

export default AdminPage;